import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { useDarkMode } from "@/shared/hooks/useDarkMode";

export interface SearchablePickerProps<T> {
  /** Array of items to choose from */
  items: T[];
  /** Currently selected value (should match what getValue returns) */
  value: string | null;
  /** Callback when selection changes */
  onChange: (value: string) => void;
  /** Extract the unique value/ID from an item */
  getValue: (item: T) => string;
  /** Extract the display label from an item */
  getLabel: (item: T) => string;
  /** Placeholder text when nothing selected */
  placeholder?: string;
  /** Placeholder text for search input */
  searchPlaceholder?: string;
  /** Message when no items match search */
  emptyMessage?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Message to show when disabled */
  disabledMessage?: string;
  /** Max items to show before searching (default: 7) */
  maxItemsBeforeSearch?: number;
  /** Width of the popover content (default: 475px) */
  popoverWidth?: string;
  /** Optional external search term control */
  searchTerm?: string;
  /** Optional external search term setter */
  onSearchChange?: (term: string) => void;
  /** Optional filter function (if not provided, filters by label) */
  filterFn?: (item: T, searchTerm: string) => boolean;
  /** Show error state (red border) for validation */
  error?: boolean;
}

export function SearchablePicker<T>({
  items,
  value,
  onChange,
  getValue,
  getLabel,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found.",
  disabled = false,
  disabledMessage,
  maxItemsBeforeSearch = 7,
  popoverWidth = "475px",
  searchTerm: externalSearchTerm,
  onSearchChange: externalOnSearchChange,
  filterFn,
  error = false,
}: SearchablePickerProps<T>) {
  const { darkMode } = useDarkMode();
  const [open, setOpen] = React.useState(false);
  const [internalSearchTerm, setInternalSearchTerm] = React.useState("");

  // Use external or internal search term
  const searchTerm = externalSearchTerm ?? internalSearchTerm;
  const setSearchTerm = externalOnSearchChange ?? setInternalSearchTerm;

  // Filter items based on search term
  const filteredItems = React.useMemo(() => {
    if (!searchTerm) return items;

    if (filterFn) {
      return items.filter((item) => filterFn(item, searchTerm));
    }

    const lowerSearch = searchTerm.toLowerCase();
    return items.filter((item) =>
      getLabel(item).toLowerCase().includes(lowerSearch)
    );
  }, [items, searchTerm, filterFn, getLabel]);

  // Find currently selected item
  const selectedItem = React.useMemo(() => {
    if (!value) return null;
    return items.find((item) => getValue(item) === value) ?? null;
  }, [items, value, getValue]);

  // Items to display (limit when not searching)
  const displayItems = searchTerm
    ? filteredItems
    : filteredItems.slice(0, maxItemsBeforeSearch);

  // Dark mode class helpers
  const darkModeButton = darkMode
    ? "text-[#abb2bf] bg-[#21252b] border-[#3e4451] hover:bg-[#3e4451] hover:text-[#abb2bf]"
    : "hover:bg-gray-100";

  const darkModePopover = darkMode
    ? "text-[#abb2bf] bg-[#21252b] border-[#3e4451]"
    : "";

  const darkModeCommand = darkMode
    ? "text-[#abb2bf] bg-[#21252b] border-[#3e4451] [&>div:nth-child(2)]:border-t [&>div:nth-child(2)]:border-[#3e4451]"
    : "";

  const darkModeInput = darkMode
    ? "text-[#abb2bf] bg-[#21252b] border-[#3e4451]"
    : "";

  const darkModeItem = darkMode
    ? "hover:!bg-[#1E4D5A] hover:!text-[#abb2bf] text-[#abb2bf] bg-[#21252b] aria-selected:!bg-[#1E4D5A] aria-selected:!text-[#abb2bf]"
    : "";

  return (
    <Popover
      open={disabled ? false : open}
      onOpenChange={disabled ? undefined : setOpen}
      modal={true}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "col-span-3 text-left justify-between font-normal overflow-hidden",
            darkModeButton,
            disabled && "opacity-50 cursor-not-allowed",
            error && darkMode && "border-red-700 bg-red-900/40",
            error && !darkMode && "border-red-500 bg-red-50"
          )}
        >
          {disabled && disabledMessage
            ? disabledMessage
            : selectedItem
            ? getLabel(selectedItem)
            : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "p-0 text-left",
          darkModePopover
        )}
        style={{ width: popoverWidth }}
      >
        <Command shouldFilter={false} className={cn(darkModeCommand)}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchTerm}
            onValueChange={setSearchTerm}
            className={cn(darkModeInput)}
          />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup className={cn(darkModePopover)}>
              {displayItems.map((item, index) => {
                const itemValue = getValue(item);
                const itemLabel = getLabel(item);

                return (
                  <CommandItem
                    key={`picker-item-${index}`}
                    value={itemLabel}
                    onSelect={() => {
                      if (itemValue === value) return;
                      onChange(itemValue);
                      setOpen(false);
                    }}
                    className={cn(darkModeItem)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === itemValue ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {itemLabel}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SearchablePicker;
